import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Repeat, CalendarBlank } from "@phosphor-icons/react";

function toIso(d) { return d.toISOString().slice(0, 10); }
const WEEKDAYS = [
  { i: 0, label: "Mon" }, { i: 1, label: "Tue" }, { i: 2, label: "Wed" },
  { i: 3, label: "Thu" }, { i: 4, label: "Fri" }, { i: 5, label: "Sat" }, { i: 6, label: "Sun" },
];

export default function Availability() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Date());
  const [days, setDays] = useState([]);
  const [weeks, setWeeks] = useState(8);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/staff/availability").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const marked = new Set(items.filter(i => i.status === "available").map(i => i.date));
  const dateStr = toIso(selected);
  const isSet = marked.has(dateStr);

  const toggle = async () => {
    try {
      if (isSet) { await api.delete(`/staff/availability/${dateStr}`); toast.success(`Removed ${dateStr}`); }
      else { await api.post("/staff/availability", { date: dateStr, start_time: "00:00", end_time: "23:59", status: "available" }); toast.success(`Available on ${dateStr}`); }
      load();
    } catch { toast.error("Could not update"); }
  };

  const toggleDay = (i) => setDays(days.includes(i) ? days.filter(x => x !== i) : [...days, i]);
  const setWeekday = async (preset) => {
    if (preset === "weekdays") setDays([0,1,2,3,4]);
    if (preset === "weekend") setDays([5,6]);
    if (preset === "all") setDays([0,1,2,3,4,5,6]);
  };
  const applyRecurring = async () => {
    if (days.length === 0) return toast.error("Pick at least one day");
    setSaving(true);
    try {
      const r = await api.post("/staff/availability/recurring", { weekdays: days, weeks_ahead: Number(weeks) });
      toast.success(`Added ${r.data.added} available days`);
      load();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <div className="label-caps">Schedule</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Your availability</h1>
        <p className="text-slate-600 mt-1">Pick specific dates, or set a recurring pattern for the weeks ahead.</p>
      </div>

      <div className="card-tactile p-4 sm:p-6 flex flex-col md:flex-row gap-6">
        <div>
          <Calendar mode="single" selected={selected} onSelect={(d)=> d && setSelected(d)}
            modifiers={{ available: (d) => marked.has(toIso(d)) }}
            modifiersClassNames={{ available: "bg-blue-600 text-white rounded-md" }} />
        </div>
        <div className="flex-1">
          <div className="label-caps mb-1">Selected date</div>
          <div className="font-display text-2xl font-bold text-slate-900">{dateStr}</div>
          <div className="mt-2 text-slate-600">Status: <strong className={isSet ? "text-emerald-600" : "text-slate-800"}>{isSet ? "Available" : "Not set"}</strong></div>
          <Button data-testid="availability-toggle-btn" onClick={toggle} className={`mt-4 ${isSet ? "bg-slate-200 text-slate-900 hover:bg-slate-300" : "bg-blue-600 hover:bg-blue-700"}`}>
            {isSet ? "Remove availability" : "Mark as available"}
          </Button>
        </div>
      </div>

      <div className="card-tactile p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3"><Repeat size={20} className="text-blue-600"/><h2 className="font-display text-xl font-bold text-slate-900">Recurring pattern</h2></div>
        <p className="text-sm text-slate-600 mb-4">Pick weekdays and how many weeks ahead to fill.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {WEEKDAYS.map((w) => (
            <button key={w.i} data-testid={`weekday-${w.label.toLowerCase()}`} type="button" onClick={()=>toggleDay(w.i)}
              className={`px-3.5 py-2 rounded-md text-sm font-semibold border transition-colors ${days.includes(w.i) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}>
              {w.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <Button size="sm" variant="outline" onClick={()=>setWeekday("weekdays")} data-testid="preset-weekdays">Weekdays</Button>
          <Button size="sm" variant="outline" onClick={()=>setWeekday("weekend")} data-testid="preset-weekend">Weekend</Button>
          <Button size="sm" variant="outline" onClick={()=>setWeekday("all")} data-testid="preset-all">Every day</Button>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="label-caps">Weeks ahead</Label>
            <Input data-testid="rec-weeks" type="number" min="1" max="52" value={weeks} onChange={(e)=>setWeeks(e.target.value)} className="h-11 mt-1.5 w-28" />
          </div>
          <Button data-testid="rec-apply-btn" onClick={applyRecurring} disabled={saving || days.length === 0} className="bg-blue-600 hover:bg-blue-700 h-11">
            <CalendarBlank size={16} className="mr-1.5" /> {saving ? "Applying…" : "Apply pattern"}
          </Button>
        </div>
      </div>
    </div>
  );
}
